import type { FileUIPart } from 'ai';
import { nanoid } from 'nanoid';

import {
  requestReplacement,
  requestSuggestion,
  subscribeReplacementStream,
  subscribeSuggestionStream,
} from '@/chat/api';
import type {
  AssistantMessage,
  ReplacementRequest,
  SuggestionMode,
  SuggestionRequest,
  UserMessage,
} from '@/chat/types';
import { uploadFile } from '@/upload/api';
import { compressImage } from '@/upload/image-compressor';
import { useChatStore } from './use-chat-store';

export const useSuggestion = (): {
  send: (input: {
    text: string | null;
    filePart: FileUIPart | null;
    isStoryboard: boolean;
    mode: SuggestionMode;
  }) => Promise<void>;
  sendReplacement: (input: { filePart: FileUIPart; text: string | null }) => Promise<void>;
  retry: (assistantMessageId: string) => Promise<void>;
} => ({ send, sendReplacement, retry });

// 턴은 순차 처리되므로 진행 중 스트림의 close는 하나만 보관 (새 대화 리셋 시 호출)
let activeStreamClose: (() => void) | null = null;

export const closeActiveStream = (): void => {
  activeStreamClose?.();
  activeStreamClose = null;
};

const send = async (input: {
  text: string | null;
  filePart: FileUIPart | null;
  isStoryboard: boolean;
  mode: SuggestionMode;
}): Promise<void> => {
  const { appendTurn, updateAssistant } = useChatStore.getState();

  const user: UserMessage = {
    id: nanoid(),
    role: 'user',
    type: 'suggestion',
    text: input.text,
    attachment: input.filePart,
    isStoryboard: input.isStoryboard,
    mode: input.mode,
  };

  let request: SuggestionRequest = {
    file: null,
    fileId: null,
    text: input.text,
    hint: input.isStoryboard ? 'storyboard' : null,
    mode: input.mode,
  };

  const assistant: AssistantMessage = {
    id: nanoid(),
    role: 'assistant',
    type: 'suggestion',
    status: input.filePart ? 'uploading' : 'analyzing',
    items: [],
    replacementItems: [],
    error: null,
    request,
  };

  appendTurn(user, assistant);

  if (input.filePart) {
    // 업로드 실패 후 재시도가 성립하도록 복원한 File을 업로드 전에 기록
    const file = await fileUIPartToFile(input.filePart);
    request = { ...request, file };
    updateAssistant(assistant.id, { request });
  }

  await runSuggestion(assistant.id, request);
};

const retry = async (assistantMessageId: string): Promise<void> => {
  const { messages, updateAssistant } = useChatStore.getState();
  const message = messages.find(
    (m): m is AssistantMessage => m.id === assistantMessageId && m.role === 'assistant'
  );
  if (!message) {
    return;
  }

  const needsUpload = !message.request.fileId && message.request.file !== null;
  updateAssistant(assistantMessageId, {
    status: needsUpload ? 'uploading' : 'analyzing',
    error: null,
  });

  if (message.type === 'replacement') {
    await runReplacement(assistantMessageId, message.request as ReplacementRequest);
  } else {
    await runSuggestion(assistantMessageId, message.request as SuggestionRequest);
  }
};

const runSuggestion = async (assistantId: string, request: SuggestionRequest): Promise<void> => {
  const { updateAssistant } = useChatStore.getState();

  try {
    let fileId = request.fileId;

    if (!fileId && request.file) {
      const fileToUpload = request.file.type.startsWith('image/')
        ? await compressImage(request.file)
        : request.file;
      const uploaded = await uploadFile(fileToUpload);
      fileId = uploaded.file_id;
      updateAssistant(assistantId, {
        status: 'analyzing',
        request: { ...request, fileId },
      });
    }

    const job = await requestSuggestion({
      file_id: fileId ?? undefined,
      text: request.text ?? undefined,
      hint: request.hint ?? undefined,
      mode: request.mode,
    });

    activeStreamClose = subscribeSuggestionStream(job, {
      onComplete: (items) => {
        activeStreamClose = null;
        updateAssistant(assistantId, { status: 'completed', items });
      },
      onError: (error) => {
        activeStreamClose = null;
        console.error('suggestion stream error:', error.message);
        updateAssistant(assistantId, { status: 'failed', error });
      },
    });
  } catch (error) {
    console.error('suggestion request error:', error);
    updateAssistant(assistantId, {
      status: 'failed',
      error: {
        message: error instanceof Error ? error.message : 'request failed',
        retryable: true,
      },
    });
  }
};

const sendReplacement = async (input: { filePart: FileUIPart; text: string | null }): Promise<void> => {
  const { appendTurn, updateAssistant } = useChatStore.getState();

  const user: UserMessage = {
    id: nanoid(),
    role: 'user',
    type: 'replacement',
    text: input.text,
    attachment: input.filePart,
    isStoryboard: false,
    mode: 'auto',
  };

  let request: ReplacementRequest = { file: null, fileId: null, text: input.text };

  const assistant: AssistantMessage = {
    id: nanoid(),
    role: 'assistant',
    type: 'replacement',
    status: 'uploading',
    items: [],
    replacementItems: [],
    error: null,
    request,
  };

  appendTurn(user, assistant);

  const file = await fileUIPartToFile(input.filePart);
  request = { ...request, file };
  updateAssistant(assistant.id, { request });

  await runReplacement(assistant.id, request);
};

const runReplacement = async (assistantId: string, request: ReplacementRequest): Promise<void> => {
  const { updateAssistant } = useChatStore.getState();

  try {
    let fileId = request.fileId;

    if (!fileId && request.file) {
      const uploaded = await uploadFile(request.file);
      fileId = uploaded.file_id;
      updateAssistant(assistantId, {
        status: 'analyzing',
        request: { ...request, fileId },
      });
    }

    const job = await requestReplacement({
      audio_file_id: fileId!,
      text: request.text ?? undefined,
    });

    activeStreamClose = subscribeReplacementStream(job, {
      onComplete: (items) => {
        activeStreamClose = null;
        updateAssistant(assistantId, { status: 'completed', replacementItems: items });
      },
      onError: (error) => {
        activeStreamClose = null;
        console.error('replacement stream error:', error.message);
        updateAssistant(assistantId, { status: 'failed', error });
      },
    });
  } catch (error) {
    console.error('replacement request error:', error);
    updateAssistant(assistantId, {
      status: 'failed',
      error: {
        message: error instanceof Error ? error.message : 'request failed',
        retryable: true,
      },
    });
  }
};

const fileUIPartToFile = async (part: FileUIPart): Promise<File> => {
  const response = await fetch(part.url);
  const blob = await response.blob();
  return new File([blob], part.filename ?? 'attachment', { type: part.mediaType });
};
